import { FormEvent, useState } from "react";
import { siteBrand } from "@/mocks/site";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dah7j9boh653ivfvnnag";

type Status = "idle" | "submitting" | "success" | "error";

type FormResponse = {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
};

export default function SubmitForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [detailLength, setDetailLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("线索已收到，编辑部会在 3 个工作日内完成初步核实。");
      form.reset();
      setDetailLength(0);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMessage = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const code = parsed?.code;
      const isSpam = String(serverMessage).toLowerCase().includes("spam");

      if (!response.ok || code !== "OK" || isSpam) {
        setStatus("error");
        setMessage(
          serverMessage && serverMessage.length < 120
            ? serverMessage
            : `提交失败，请稍后再试，或直接发邮件到 ${siteBrand.email}。`,
        );
        return;
      }

      setStatus("success");
      setMessage("线索已收到，编辑部会在 3 个工作日内完成初步核实。");
      form.reset();
      setDetailLength(0);
    } catch {
      setStatus("error");
      setMessage("网络似乎不太稳定，提交没有成功，请稍后再试。");
    }
  };

  const inputClass =
    "mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";

  return (
    <form
      id="dah7j9boh653ivfvnnag"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
    >
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="nickname" className="text-sm font-medium text-foreground-800">
              你的称呼
            </label>
            <input
              id="nickname"
              name="nickname"
              type="text"
              required
              placeholder="可以填昵称"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="email" className="text-sm font-medium text-foreground-800">
              联系邮箱
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              placeholder="用于回访核实，不会公开"
              className={inputClass}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="category" className="text-sm font-medium text-foreground-800">
              线索属于哪一类
            </label>
            <select id="category" name="category" required defaultValue="" className={inputClass}>
              <option value="" disabled>
                请选择一个分类
              </option>
              <option value="娱乐事件">娱乐事件</option>
              <option value="影视宣发">影视宣发</option>
              <option value="综艺幕后">综艺幕后</option>
              <option value="品牌代言">品牌代言</option>
              <option value="音乐现场">音乐现场</option>
              <option value="网络热点">网络热点</option>
              <option value="其他线索">其他线索</option>
            </select>
          </div>
          <div>
            <label htmlFor="event_time" className="text-sm font-medium text-foreground-800">
              事件大致时间
            </label>
            <input
              id="event_time"
              name="event_time"
              type="text"
              placeholder="例如：8 月 20 日晚间"
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label htmlFor="detail" className="text-sm font-medium text-foreground-800">
            线索详情
          </label>
          <textarea
            id="detail"
            name="detail"
            required
            rows={5}
            maxLength={500}
            onChange={(event) => setDetailLength(event.target.value.length)}
            placeholder="请尽量写清楚时间、地点与可核对的材料，例如截图、物料链接或现场记录。"
            className={`${inputClass} resize-none`}
          />
          <p className="mt-2 flex items-center justify-between text-xs text-foreground-500">
            <span>涉及个人隐私的内容，我们会先做脱敏处理。</span>
            <span>{detailLength} / 500</span>
          </p>
        </div>

        <div className="field-aux-note" aria-hidden="true">
          <label htmlFor="contact_alt">请勿填写此项</label>
          <input
            id="contact_alt"
            name="contact_alt"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            readOnly
          />
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="submit"
            disabled={status === "submitting"}
            className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
            {status === "submitting" ? "正在提交" : "提交线索"}
          </button>
          <p className="text-xs leading-relaxed text-foreground-500">
            提交即表示你同意我们在必要时就线索内容与你联系。
          </p>
        </div>

        {status === "success" && message ? (
          <p className="flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3 text-sm text-secondary-900">
            <i className="ri-checkbox-circle-line mt-0.5"></i>
            {message}
          </p>
        ) : null}

        {status === "error" && message ? (
          <p className="flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-800">
            <i className="ri-error-warning-line mt-0.5"></i>
            {message}
          </p>
        ) : null}
      </div>
    </form>
  );
}